import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { revokeSession, storeTokens, TOKENS_REFRESHED_EVENT } from "../lib/authSession";

const STORAGE_TOKEN = "authToken";
const STORAGE_USER = "userInfo";

const AuthContext = createContext(undefined);

function readStoredSession() {
  try {
    const token = localStorage.getItem(STORAGE_TOKEN);
    const raw = localStorage.getItem(STORAGE_USER);
    if (!token || !raw) return { token: null, user: null };
    return { token, user: JSON.parse(raw) };
  } catch {
    return { token: null, user: null };
  }
}

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const { token: t, user: u } = readStoredSession();
    setToken(t);
    setUser(u);
    setHydrated(true);
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_TOKEN && e.key !== STORAGE_USER) return;
      const { token: t, user: u } = readStoredSession();
      setToken(t);
      setUser(u);
    };
    // Token vừa được làm mới (tab này) → cập nhật để component không gửi token cũ
    const onRefreshed = () => setToken(localStorage.getItem(STORAGE_TOKEN));
    window.addEventListener("storage", onStorage);
    window.addEventListener(TOKENS_REFRESHED_EVENT, onRefreshed);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(TOKENS_REFRESHED_EVENT, onRefreshed);
    };
  }, []);

  const emitAuthChanged = () => {
    try {
      window.dispatchEvent(new CustomEvent("dalat-auth-changed"));
    } catch {
      window.dispatchEvent(new Event("dalat-auth-changed"));
    }
  };

  const login = useCallback(({ token: newToken, refreshToken, user: newUser }) => {
    if (!newToken || !newUser) return;
    localStorage.setItem(STORAGE_USER, JSON.stringify(newUser));
    storeTokens({ token: newToken, refreshToken });
    setToken(newToken);
    setUser(newUser);
    emitAuthChanged();
  }, []);

  const logout = useCallback(() => {
    revokeSession();
    localStorage.removeItem(STORAGE_TOKEN);
    localStorage.removeItem(STORAGE_USER);
    setToken(null);
    setUser(null);
    emitAuthChanged();
    navigate("/", { replace: false });
  }, [navigate]);

  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      const next =
        typeof patch === "function"
          ? patch(prev)
          : { ...(prev || {}), ...patch };
      if (next && Object.keys(next).length) {
        localStorage.setItem(STORAGE_USER, JSON.stringify(next));
        return next;
      }
      return prev;
    });
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token && user),
      hydrated,
      login,
      logout,
      updateUser,
    }),
    [user, token, hydrated, login, logout, updateUser]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
